import React, { useEffect, useState } from 'react';
import useForm from '@/hooks/useForm';
import { useConfirmDelete } from '@/hooks/useConfirmDelete';
import validators from '@/helpers/validators';
import { toast } from 'react-toastify';
import api, { AxiosError } from '@/services/api';
import Pos from '@/model/Pos';
import { useDialog } from '@/hooks/useDialog';
import { DeleteContent } from '@/components/DeleteContent';
import { TabPdvActionsProps } from '@/features/registerEvent/screens/Pdv/ui';
import { useParams } from 'react-router-dom';
import Pdv from '@/model/Pdv';
import CardFees from '@/model/CardFees';
import EventPdvPos from '@/model/EventPdvPos';
import { unmask } from '@/helpers/masks/cashNumber';
import { toPercentage } from '@/helpers/common/amount';
import EventPos from '@/model/EventPos';
import EventSection from '@/model/EventSection';
import Section from '@/model/Section';
import Product from '@/model/Product';
import { getItem } from '@/helpers/common/localStorage';
import { REACT_APP_USER } from '@/utils/config';
import { AuthUser } from '@/model/AuthUser';
import {
  formPosConfigProps,
  formPosProps,
  formPosRegisterProps,
  modalConfigPosSettingsProps,
  onShouldShowPosSettingsProps,
  posActionsProps,
} from '../types';
import { States, PdvEventPosContainer, ShouldShowModal, FormInputName } from './ui';
import { FormInputName as FormInputNameRegister } from '../components/PosContent';
import { FormInputName as FormInputNameConfig } from '../components/PosConfigContent';

type UrlParams = {
  id: string;
};

interface PdvEventPosScreen extends TabPdvActionsProps {
  pdvId?: string;
  handleGetEventPhaseCompletion: () => void;
}

export const PdvEventPosScreen: React.FC<Omit<PdvEventPosScreen, 'firstTab'>> = ({
  pdvId,
  handleGetEventPhaseCompletion,
  backTab,
  nextTab,
}): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [enableFee, setEnableFee] = useState<boolean>(false);

  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(
    ShouldShowModal.configProduct,
  );

  const [pos, setPos] = useState<Pos>();
  const [posList, setPosList] = useState<EventPdvPos[]>([]);
  const [posOptions, setPosOptions] = useState<Pos[]>([]);
  const [showRecharge, setShowRecharge] = useState<boolean>(false);
  const [eventSections, setEventSections] = useState<EventSection[]>([]);

  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const confirmDelete = useConfirmDelete();

  const params = useParams<UrlParams>();

  const {
    formData: formDataPos,
    formErrors: formErrorsPos,
    onChangeFormInput: onChangeFormInputPos,
    isFormValid: isFormValidPos,
  } = useForm({
    initialData: {
      hasPos: 'false',
    },
    validators: {
      hasPos: [validators.required],
    },
    formatters: {},
  });

  const {
    formData: formDataPosRegister,
    formErrors: formErrorsPosRegister,
    onChangeFormInput: onChangeFormInputPosRegister,
    isFormValid: isFormValidPosRegister,
    resetForm: resetFormPosRegister,
  } = useForm({
    initialData: {
      name: '',
      singlePayment: 'false',
      printReceipt: 'false',
      rechargePrepaidCard: 'false',
      chargePrepaidCard: 'false',
      rechargeSection: '',
      rechargeProduct: '',
      waiter: '',
      commission: '',
      posReplacement: '',
      isPosReplacement: 'false',
    },
    validators: {
      name: [validators.required],
      singlePayment: [validators.required],
      rechargePrepaidCard: [validators.required],
      waiter: [validators.required],
      commission: [validators.required],
    },
    formatters: {},
  });

  const {
    formData: formDataPosConfig,
    formErrors: formErrorsPosConfig,
    onChangeFormInput: onChangeFormInputPosConfig,
    isFormValid: isFormValidPosConfig,
    resetForm: resetFormPosConfig,
  } = useForm({
    initialData: {
      physicalSaleDebit: '',
      physicalSaleCredit: '',
      physicalSalePix: '',
      physicalSaleAdministrateTax: '',
      physicalSaleInstallments: '',
      physicalSaleFee: '',
    },
  });

  // modal config ------------------------------------------------------------
  const handleOnShouldShowModal = ({
    value,
    newTitleModal,
    pos: posSelected,
  }: onShouldShowPosSettingsProps): void => {
    if (posSelected.cardFees) {
      onChangeFormInputPosConfig(FormInputNameConfig.physicalSaleDebit)(
        posSelected.cardFees.debit ? toPercentage(posSelected.cardFees.debit) : '',
      );
      onChangeFormInputPosConfig(FormInputNameConfig.physicalSaleCredit)(
        posSelected.cardFees.credit ? toPercentage(posSelected.cardFees.credit) : '',
      );
      onChangeFormInputPosConfig(FormInputNameConfig.physicalSalePix)(
        posSelected.cardFees.pix ? toPercentage(posSelected.cardFees.pix) : '',
      );
      onChangeFormInputPosConfig(FormInputNameConfig.physicalSaleAdministrateTax)(
        posSelected.cardFees.administrateTax
          ? toPercentage(posSelected.cardFees.administrateTax)
          : '',
      );
      onChangeFormInputPosConfig(FormInputNameConfig.physicalSaleInstallments)(
        posSelected.cardFees.installments ? String(posSelected.cardFees.installments) : '',
      );
      onChangeFormInputPosConfig(FormInputNameConfig.physicalSaleFee)(
        posSelected.cardFees.fee ? toPercentage(posSelected.cardFees.fee) : '',
      );
    }
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();

    if (posSelected.pos.id && value === ShouldShowModal.configProduct) {
      setPos(posSelected.pos);
    }
  };

  const getPos = async (): Promise<void> => {
    if (pdvId) {
      const response = await api.get<EventPdvPos[]>(`/event/pdv/${params.id}/pos/${pdvId}/`);
      if (response.data.length > 0) {
        onChangeFormInputPos(FormInputName.hasPos)('true');
      }
      setPosList(response.data);
    }
    handleGetEventPhaseCompletion();
  };

  const handleOnConfirmDelete = async (posSelected: Pos): Promise<void> => {
    try {
      setState(States.loading);
      await api.delete(`/event/pdv/${params.id}/pos/${pdvId}/${posSelected.id}`);
      await getPos();
      toast.success('POS excluída com sucesso!');
      confirmDelete.hide();
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleHasPos = async (b: string): Promise<void> => {
    try {
      handleGetEventPhaseCompletion();
      setState(States.loading);
      await api.patch(`/event/pdv/${params.id}/pos/${pdvId}/has/${b}`);
    } finally {
      setState(States.default);
    }
  };

  const handleOnShowDeletePos = (posSelected: Pos): void => {
    confirmDelete.show({
      title: '',
      children: <DeleteContent />,
      actions: [
        {
          title: 'Não, quero manter',
          theme: 'noneBorder',
          onClick: (): void => confirmDelete.hide(),
        },
        {
          title: 'Sim, quero excluir',
          onClick: (): void => {
            handleOnConfirmDelete(posSelected);
          },
        },
      ],
    });
  };

  const controllerModalConfig: modalConfigPosSettingsProps = {
    title,
    visible,
    onChangeTitle,
    onToggle,
    onShouldShowModal: handleOnShouldShowModal,
    shouldShowModal,
    onShowModalDelete: handleOnShowDeletePos,
  };
  // modal config ------------------------------------------------------------

  const handleOnCheckAlterFeePermission = (): void => {
    let found = false;
    const user = getItem(String(REACT_APP_USER)) as AuthUser;
    if (user && user.roles && user.roles.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < user.roles.length; i++) {
        if (user.roles[i] === 'Adm-Alter-Fee-Event') {
          found = true;
          break;
        }
      }
    }
    setEnableFee(found);
  };

  const handleGetAllPos = async (): Promise<void> => {
    try {
      setState(States.loading);
      await getPos();
      const { data } = await api.get<Pos[]>('/pos/find');
      setPosOptions(data ?? []);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleNextTab = async (): Promise<void> => {
    if (isFormValidPos()) {
      nextTab();
    }
  };

  const handleBackTab = (): void => {
    backTab();
  };

  const handleOnGetSections = async (posId: string): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<EventPos[]>(`/event/section-product/${params.id}/pos`);
      if (data && data.length > 0) {
        // eslint-disable-next-line no-plusplus
        for (let p = 0; p < data.length; p++) {
          if (data[p].pos.id === posId) {
            if (data[p].eventSections && data[p].eventSections.length > 0) {
              setEventSections(data[p].eventSections);
            }
            break;
          }
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleOnGetPos = async (posSelected: EventPdvPos): Promise<void> => {
    if (posSelected) {
      setPos(posSelected.pos);

      onChangeFormInputPosRegister(FormInputNameRegister.name)(posSelected.pos.id as string);
      onChangeFormInputPosRegister(FormInputNameRegister.singlePayment)(
        `${posSelected.singlePayment}`,
      );
      if (posSelected.printReceipt) {
        onChangeFormInputPosRegister(FormInputNameRegister.printReceipt)(
          `${posSelected.printReceipt}`,
        );
      }
      onChangeFormInputPosRegister(FormInputNameRegister.rechargePrepaidCard)(
        `${posSelected.rechargePrepaidCard}`,
      );
      if (posSelected.rechargePrepaidCard) {
        await handleOnGetSections(posSelected.pos.id as string);
        setShowRecharge(true);
        setTimeout(() => {
          onChangeFormInputPosRegister(FormInputNameRegister.chargePrepaidCard)(
            `${posSelected.chargePrepaidCard}`,
          );
          if (posSelected.rechargeSection) {
            onChangeFormInputPosRegister(FormInputNameRegister.rechargeSection)(
              `${posSelected.rechargeSection.id}`,
            );
          }
          if (posSelected.rechargeProduct) {
            onChangeFormInputPosRegister(FormInputNameRegister.rechargeProduct)(
              `${posSelected.rechargeProduct.id}`,
            );
          }
        }, 200);
      }

      onChangeFormInputPosRegister(FormInputNameRegister.waiter)(toPercentage(posSelected.waiter));
      onChangeFormInputPosRegister(FormInputNameRegister.commission)(
        toPercentage(posSelected.commission),
      );
    }
  };

  const handleOnChangeRecharge = async (value: string): Promise<void> => {
    onChangeFormInputPosRegister(FormInputNameRegister.rechargePrepaidCard)(value);
    setShowRecharge(value === 'true');
    if (value === 'true') {
      await handleOnGetSections(formDataPosRegister[FormInputNameRegister.name]);
    }
  };

  const handleOnCancelEditPos = (): void => {
    try {
      setPos(undefined);
      resetFormPosRegister();
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    }
  };

  const handleInsertPos = async (): Promise<void> => {
    if (isFormValidPosRegister()) {
      try {
        setState(States.loading);
        /*
        chargePrepaidCard?: boolean;
        rechargeSection?: Section;
        rechargeProduct?: Product;
        */
        const singlePayment = formDataPosRegister[FormInputNameRegister.singlePayment]
          ? formDataPosRegister[FormInputNameRegister.singlePayment] === 'true'
          : undefined;
        let printReceipt: boolean | undefined;
        if (singlePayment) {
          printReceipt = formDataPosRegister[FormInputNameRegister.printReceipt] === 'true';
        }
        const chargePrepaidCard = formDataPosRegister[FormInputNameRegister.rechargePrepaidCard]
          ? formDataPosRegister[FormInputNameRegister.rechargePrepaidCard] === 'true'
          : undefined;

        const rechargeSection = formDataPosRegister[FormInputNameRegister.rechargeSection]
          ? ({ id: formDataPosRegister[FormInputNameRegister.rechargeSection] } as Section)
          : undefined;
        const rechargeProduct = formDataPosRegister[FormInputNameRegister.rechargeProduct]
          ? ({ id: formDataPosRegister[FormInputNameRegister.rechargeProduct] } as Product)
          : undefined;
        const pdv = {
          id: pdvId,
        } as Pdv;
        const posReq = {
          id: formDataPosRegister[FormInputNameRegister.name],
        } as Pos;
        const posReplacementReq = formDataPosRegister[FormInputNameRegister.posReplacement]
          ? ({ id: formDataPosRegister[FormInputNameRegister.posReplacement] } as Pos)
          : undefined;

        const request = {
          pdv,
          pos: posReq,
          singlePayment,
          printReceipt,
          rechargePrepaidCard:
            formDataPosRegister[FormInputNameRegister.rechargePrepaidCard] === 'true',
          chargePrepaidCard,
          rechargeSection,
          rechargeProduct,
          waiter: unmask(formDataPosRegister[FormInputNameRegister.waiter]) as unknown as number,
          commission: unmask(
            formDataPosRegister[FormInputNameRegister.commission],
          ) as unknown as number,
          posReplacement: posReplacementReq,
        } as EventPdvPos;
        await api.post<EventPdvPos>(`/event/pdv/${params.id}/pos/`, request);
        resetFormPosRegister();
        await getPos();
        toast.success('POS cadastrada com sucesso!');
      } catch (error) {
        const err = error as AxiosError;
        toast.error(err.message);
      } finally {
        setState(States.default);
      }
    }
  };

  const handleOnSavePosConfg = async (): Promise<void> => {
    if (isFormValidPosConfig()) {
      try {
        setState(States.loading);
        const cardFees = {
          debit: +unmask(formDataPosConfig[FormInputNameConfig.physicalSaleDebit]),
          credit: +unmask(formDataPosConfig[FormInputNameConfig.physicalSaleCredit]),
          pix: +unmask(formDataPosConfig[FormInputNameConfig.physicalSalePix]),
          administrateTax: +unmask(
            formDataPosConfig[FormInputNameConfig.physicalSaleAdministrateTax],
          ),
          installments: Number(formDataPosConfig[FormInputNameConfig.physicalSaleInstallments]),
          fee: +unmask(formDataPosConfig[FormInputNameConfig.physicalSaleFee]),
        } as CardFees;

        await api.post(`/event/pdv/${params.id}/pos/${pdvId}/fee/${pos?.id}`, cardFees);

        toast.success('Configurações salvas com sucesso!');

        resetFormPosConfig();
        handleOnCancelEditPos();
        onToggle();
        await handleGetEventPhaseCompletion();
        await getPos();
      } catch (error) {
        const err = error as AxiosError;
        toast.error(err.message);
      } finally {
        setState(States.default);
      }
    }
  };

  const controllerFormPos: formPosProps = {
    formData: formDataPos,
    formErrors: formErrorsPos,
    onChangeFormInput: onChangeFormInputPos,
    onInsertPos: handleInsertPos,
    isFormValid: isFormValidPos,
  };

  const controllerFormPosRegister: formPosRegisterProps = {
    formData: formDataPosRegister,
    formErrors: formErrorsPosRegister,
    onChangeFormInput: onChangeFormInputPosRegister,
    isFormValid: isFormValidPosRegister,
  };

  const controllerFormPosConfig: formPosConfigProps = {
    formData: formDataPosConfig,
    formErrors: formErrorsPosConfig,
    onChangeFormInput: onChangeFormInputPosConfig,
    onSave: handleOnSavePosConfg,
    isFormValid: isFormValidPosConfig,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const controllerPosStates: any = {
    pos,
    setPos,
    posList,
    setPosList,
    posOptions,
    setPosOptions,
  };

  const controllerPosActions: posActionsProps = {
    // onSave: () => Promise<void>;
    onGet: handleOnGetPos,
    onCancelEdit: handleOnCancelEditPos,
    onReturnTap: handleBackTab,
    onNextTap: handleNextTab,
  };

  useEffect(() => {
    getPos();
  }, [pdvId]);

  useEffect(() => {
    handleOnCheckAlterFeePermission();
    handleGetAllPos();
  }, []);

  return (
    <PdvEventPosContainer
      state={state}
      enableFee={enableFee}
      formPos={controllerFormPos}
      formPosRegister={controllerFormPosRegister}
      showRecharge={showRecharge}
      eventSections={eventSections}
      formPosConfig={controllerFormPosConfig}
      posStates={controllerPosStates}
      posActions={controllerPosActions}
      modalConfig={controllerModalConfig}
      handleHasPos={handleHasPos}
      onChangeRecharge={handleOnChangeRecharge}
    />
  );
};
